/**
 * Build-readiness resolution for the onboarding chat (client-side).
 *
 * The "Yes, let's build!" confirmation used to gate purely on the client's
 * local delta accumulation. That broke the moment capture became partly
 * server-side: the deterministic materials extraction (materialsCapture.ts)
 * merges deck/site facts into the session row, and the delta-emitting model —
 * seeing [CAPTURED_SO_FAR] — correctly stops re-emitting fields it considers
 * captured. Result: the model declares "complete", the SERVER result is
 * complete, but the client's local snapshot never satisfies
 * hasCompleteBigIdeaData and the CTA never appears (caught live in the
 * 2026-08-20 Therassist deck-only eval).
 *
 * Fix: readiness is resolved against the richest available view — local
 * first (zero-latency fast path for typed flows), then local merged over the
 * server's canonical result (GET /api/onboarding/session).
 */

import { captureClientError } from "@/lib/sentry/capture-client";
import {
  hasCompleteBigIdeaData,
  looksLikeOnboardingResult,
  mergePartialResults,
  mergeWithDefaults,
  type OnboardingResult,
  type PartialOnboardingResult,
} from "@/lib/onboarding/schema";

/**
 * Fetch the canonical merged onboarding result for the current session cookie.
 * Returns null on ANY failure — the caller falls back to local-only gating.
 */
export async function fetchOnboardingSessionResult(): Promise<PartialOnboardingResult | null> {
  try {
    const res = await fetch("/api/onboarding/session", {
      method: "GET",
      credentials: "same-origin",
    });
    if (!res.ok) {
      // 404 (no session) and 409 (claimed — flow moved on) are normal
      // outcomes. Everything else (429 budget, 5xx) is a FAILURE that costs a
      // deck-only founder the CTA — per the sentinel rule it must be visible,
      // not collapsed into the same null as "no session".
      if (res.status !== 404 && res.status !== 409) {
        captureClientError(new Error(`Onboarding session read failed: HTTP ${res.status}`), {
          feature: "onboarding-chat",
          errorType: "session-result-read",
          impact: "degraded",
          httpStatus: res.status,
        });
      }
      return null;
    }
    const data = (await res.json()) as { result?: unknown };
    return looksLikeOnboardingResult(data?.result) ? data.result : null;
  } catch (err) {
    // Degraded, not blocking: readiness falls back to the local snapshot,
    // which still works for typed-idea flows — but a deck-only founder loses
    // the CTA, so this must be visible in Sentry.
    captureClientError(err, {
      feature: "onboarding-chat",
      errorType: "session-result-read",
      impact: "degraded",
    });
    return null;
  }
}

/**
 * Resolve the full OnboardingResult to build with, or null when readiness is
 * not met. Local-complete short-circuits without a network call; otherwise the
 * server's canonical result is merged UNDER the local snapshot (the local
 * stream is fresher where it emitted — its persist is fire-and-forget and may
 * not have landed yet).
 */
export async function resolveCompletedOnboardingResult(
  stableResult: PartialOnboardingResult | OnboardingResult | null | undefined,
  fetchServerResult: () => Promise<PartialOnboardingResult | null> = fetchOnboardingSessionResult,
): Promise<OnboardingResult | null> {
  if (hasCompleteBigIdeaData(stableResult)) {
    return mergeWithDefaults(stableResult);
  }
  const serverResult = await fetchServerResult();
  if (!serverResult) return null;
  const merged = mergePartialResults(serverResult, stableResult ?? undefined);
  if (hasCompleteBigIdeaData(merged)) {
    return merged;
  }
  return null;
}
